/**
 * Data layer for `capi-event-quality-score-monitoring`. Thin typed fetch wrappers over
 * /api/v1/capi-event-quality-score-monitoring. Raw fetch() is confined to this file per dash contract.
 */

export interface CapiEventQualityScoreMonitoringRow {
	id: number;
	tenant_id: string;
	event_id: string;
	event_name: string;
	match_quality_score: number;
	match_rate: number | null;
	event_count: number;
	window_start: number;
	window_end: number;
	pixel_id: string | null;
	payload_json: string;
	created_at: number;
}

export interface ListCapiEventQualityScoreMonitoringResponse {
	count: number;
	items: CapiEventQualityScoreMonitoringRow[];
}

export interface CreateCapiEventQualityScoreMonitoringBody {
	event_id: string;
	event_name: string;
	match_quality_score: number;
	match_rate?: number | null;
	event_count?: number;
	window_start: number;
	window_end: number;
	pixel_id?: string | null;
}

export interface CreateCapiEventQualityScoreMonitoringResponse {
	ok: boolean;
	event_id: string;
	event_name: string;
	match_quality_score: number;
	created_at: number;
}

const BASE = "/api/v1/capi-event-quality-score-monitoring";

async function parseOrThrow<T>(res: Response): Promise<T> {
	const text = await res.text();
	let data: unknown = null;
	if (text) {
		try {
			data = JSON.parse(text);
		} catch {
			data = text;
		}
	}
	if (!res.ok) {
		const msg =
			data &&
			typeof data === "object" &&
			data !== null &&
			"error" in data &&
			typeof (data as { error: unknown }).error === "string"
				? (data as { error: string }).error
				: `HTTP ${res.status}`;
		throw new Error(msg);
	}
	return data as T;
}

export async function list(): Promise<ListCapiEventQualityScoreMonitoringResponse> {
	const res = await fetch(BASE, {
		method: "GET",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<ListCapiEventQualityScoreMonitoringResponse>(res);
}

export async function create(
	body: CreateCapiEventQualityScoreMonitoringBody,
): Promise<CreateCapiEventQualityScoreMonitoringResponse> {
	const res = await fetch(BASE, {
		method: "POST",
		credentials: "include",
		headers: {
			accept: "application/json",
			"content-type": "application/json",
		},
		body: JSON.stringify(body),
	});
	return parseOrThrow<CreateCapiEventQualityScoreMonitoringResponse>(res);
}

export const capiEventQualityScoreMonitoringQueryKeys = {
	all: ["capi-event-quality-score-monitoring"] as const,
	list: () => [...capiEventQualityScoreMonitoringQueryKeys.all] as const,
	detail: (id: string) => [...capiEventQualityScoreMonitoringQueryKeys.all, id] as const,
};
